import { CoreDataStore } from "./CoreDataStore";
import { observable, action, runInAction, ObservableMap, makeObservable } from "mobx";
import { handleStoreState } from "../RESTStore/CoreStore";
import { API } from "General/API";

export abstract class MapDataStore<K extends string | number, T extends object> extends CoreDataStore {
  data: ObservableMap<K, T>;

  constructor(options) {
    super(options);

    makeObservable(this, {
      data: observable,
      clear: action,
      add: action.bound,
      edit: action.bound,
      delete: action.bound,
      fetch: action.bound,
    });
  }

  clear() {
    if (this.data) {
      this.data.clear();
    } else {
      this.data = new Map() as ObservableMap<K, T>;
    }
    this.state = "RAW";
    this.stateError = undefined;
  }

  // method to generate map key
  abstract mapKey(params?: object): K;

  @handleStoreState()
  async fetch(queryParams?: object) {
    try {
      const response = await API.getData<T[]>(this.url(), queryParams);
      runInAction(() => {
        const responseKeys = new Set<K>();
        response.forEach((item) => {
          const key = this.mapKey(item);
          responseKeys.add(key);
          this.data.set(key, this.processResponse(item));
        });

        const staleKeys: K[] = [];
        this.data.forEach((_value, key) => {
          if (!responseKeys.has(key)) {
            staleKeys.push(key);
          }
        });
        staleKeys.forEach((key) => this.data.delete(key));
      });
    } catch (error) {
      this.processError(error);
      throw error;
    }
  }

  find(id: K) {
    return this.data.get(id);
  }

  add(newData: T): void {
    this.edit(this.mapKey(), newData);
  }

  edit(id: K, newData: T) {
    this.data.set(id, newData);
  }

  delete(id: K) {
    this.data.delete(id);
  }
}
